import { saveAs } from "file-saver";

import { GET_ATTACHMENT } from "graphql/query/attachment-gql";

const attachmentError = (key) => {
    const error = new Error(key);
    error.attachmentErrorKey = key;
    return error;
};

const abortError = () => {
    const error = new Error("Attachment opening cancelled");
    error.name = "AbortError";
    return error;
};

export const getAttachmentPath = (id) => `/attachments/${encodeURIComponent(id)}`;

export const getAttachmentFileName = (attachment) => {
    const name = attachment.title || attachment.name || "attachment";
    const extension = (attachment.extension || "").replace(/^\./, "");

    if (!extension || name.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
        return name;
    }

    return `${name}.${extension}`;
};

export const isPdfAttachment = (attachment) =>
    (attachment.extension || "").toLowerCase() === "pdf";

export const getFreshAttachment = async (client, id) => {
    const { data } = await client.query({
        query: GET_ATTACHMENT,
        variables: { id },
        fetchPolicy: "no-cache",
        context: { fetchOptions: { cache: "no-store" } }
    });

    const attachment = data && data.attachment;

    if (!attachment) {
        throw attachmentError("ATTACHMENTS.Error_Unavailable");
    }

    const url = attachment.display_url;
    let validUrl = false;

    if (typeof url === "string") {
        try {
            const parsed = new URL(url);
            validUrl = parsed.protocol === "http:" || parsed.protocol === "https:";
        } catch (error) {
            validUrl = false;
        }
    }

    if (!validUrl) {
        throw attachmentError("ATTACHMENTS.Error_MissingUrl");
    }

    return attachment;
};

export const getAttachmentErrorKey = (error) =>
    (error && error.attachmentErrorKey) || "ATTACHMENTS.Error_Generic";

export const openAttachment = async (client, attachment, options = {}) => {
    const {
        download = false,
        isMobile = window.innerWidth < 768,
        signal,
        isActive = () => true,
        navigate = (path) => window.location.assign(path),
        onPreviewWindow = () => {}
    } = options;

    const checkActive = () => {
        if ((signal && signal.aborted) || !isActive()) {
            throw abortError();
        }
    };

    let previewWindow = null;

    const releasePreview = (close) => {
        if (previewWindow) {
            if (close && !previewWindow.closed) {
                previewWindow.close();
            }
            previewWindow = null;
            onPreviewWindow(null);
        }
    };

    try {
        checkActive();

        if (!download && !isMobile && isPdfAttachment(attachment)) {
            // Reserve the tab during the click, before awaiting the signed URL.
            previewWindow = window.open("", "_blank");

            if (!previewWindow) {
                checkActive();
                navigate(getAttachmentPath(attachment.id));
                return;
            }

            previewWindow.opener = null;
            onPreviewWindow(previewWindow);
        }

        const freshAttachment = await getFreshAttachment(client, attachment.id);
        checkActive();

        if (previewWindow) {
            if (!previewWindow.closed) {
                previewWindow.location.href = freshAttachment.display_url;
            }
            releasePreview(false);
            return;
        }

        const response = await fetch(freshAttachment.display_url, {
            credentials: "omit",
            cache: "no-store",
            signal
        });
        checkActive();

        if (!response.ok) {
            throw attachmentError("ATTACHMENTS.Error_Download");
        }

        const blob = await response.blob();
        checkActive();
        saveAs(blob, getAttachmentFileName(freshAttachment));
    } catch (error) {
        releasePreview(true);
        throw error;
    }
};
